/**
 * Conditional product section for partner pages.
 * Routes to the appropriate layout based on the partner's productType from Contentful:
 *   - "CRM" -> CRMProductSection (dashboard + feature grid)
 *   - "Marketing" -> MarketingProductSection (stat cards + AI assistant)
 *   - "Both" (default) -> PlatformDemoSection (tabbed CRM + Marketing demo)
 *
 * Does not own data fetching. Depends on: CRMProductSection, MarketingProductSection, PlatformDemoSection.
 */
import { BookOpen, Briefcase, Calendar, CreditCard, Globe, Layout, MapPin, PenLine, Share2, Star } from "lucide-react";

import PlatformDemoSection from "@/components/marketing/PlatformDemoSection";
import CRMProductSection from "@/components/partners/CRMProductSection";
import MarketingProductSection from "@/components/partners/MarketingProductSection";

import type { PlatformTab } from "@/components/marketing/PlatformDemoSection";
import type { PartnerProductType } from "@/pages/partners/[partnerName]";

interface ProductsProps {
  productType?: PartnerProductType;
}

const PARTNER_PLATFORM_TABS: PlatformTab[] = [
  {
    id: "full-ops",
    label: "Full Operations Suite",
    shortLabel: "Full",
    headline: "7 subscriptions in one.",
    description: "Turn completed jobs into your best salesperson. Marketing & CRM working together.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/dashboard.png?updatedAt=1773548951965",
    leftPhones: [
      {
        src: "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/reviews.png?updatedAt=1773548952013",
        alt: "Reviews",
      },
    ],
    rightPhones: [
      {
        src: "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/quotes.png?updatedAt=1773548952015",
        alt: "Quotes",
      },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    shortLabel: "CRM",
    headline: "Estimate. Schedule. Get Paid.",
    description: "Capture leads, manage communications, send quotes, close jobs, invoice clients, and collect reviews.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/invoices.png?updatedAt=1773548951959",
    leftCards: [
      { icon: Calendar, title: "Appointments", description: "Track active jobs and progress from start to finish." },
      {
        icon: BookOpen,
        title: "Quotes & Pricebook",
        description: "Create and send professional estimates within minutes.",
      },
      { icon: CreditCard, title: "Invoicing & Payments", description: "Send invoices and collect payments with ease." },
    ],
    rightCards: [
      { icon: Briefcase, title: "Jobs", description: "Track active jobs and progress from start to finish." },
      {
        icon: Globe,
        title: "Client Management",
        description: "Organize contacts, notes, and communication in one place.",
      },
      { icon: Share2, title: "Client Portal", description: "Share job details, estimates, and updates with clients." },
    ],
  },
  {
    id: "marketing-kit",
    label: "Marketing Kit",
    shortLabel: "Marketing",
    headline: "Online presence, boosted by AI.",
    description: "Website, social media, and review management, continuously optimized by AI.",
    phoneImage:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Product%20Screenshots/review-response.png?updatedAt=1773548951961",
    leftCards: [
      {
        icon: Layout,
        title: "AI Website Builder",
        description: "Generate, customize, and publish SEO-optimized sites in 24 hours.",
      },
      { icon: Share2, title: "Social Media Manager", description: "Post, geotag across platforms (Desktop & Mobile)." },
      { icon: MapPin, title: "Google Business Profile Optimizer", description: "Automated GBP management." },
    ],
    rightCards: [
      { icon: Star, title: "Review Management", description: "Automate the review loop, replace standalone tools." },
      { icon: MapPin, title: "Projects Geotagging", description: "Showcase completed work with location data." },
      {
        icon: PenLine,
        title: "Auto-Blogging",
        description: "Automated blog post suggestions drafted by AI based on your data.",
      },
    ],
  },
];

const PARTNER_PLATFORM_CONTENT = {
  badge: "The Platform",
  heading: (
    <>
      One platform. All the tools
      <br className="hidden md:block" /> you need to <span className="text-clay-400">win.</span>
    </>
  ),
  ctaLabel: "Learn More",
};

export default function Products({ productType }: ProductsProps) {
  if (productType === "CRM") return <CRMProductSection />;
  if (productType === "Marketing") return <MarketingProductSection />;

  return <PlatformDemoSection tabs={PARTNER_PLATFORM_TABS} content={PARTNER_PLATFORM_CONTENT} />;
}
